import { DownOutlined, PrinterOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRankingStar, faAsterisk } from '@fortawesome/free-solid-svg-icons'
import { Checkbox, Col, Dropdown, Menu, Modal, Radio, Row, Select, Space, Table, notification } from 'antd'
import Button from 'antd-button-color'
import React, { useEffect, useRef, useState } from 'react'
import _ from 'lodash'

import './css/tournament-pools-scoring.css'
import PoolScoreMatrix from './PoolScoreMatrix'
import useWindowDimensions from '../../../../hooks/getWindowDimensions'
import { squadronHelper } from '../../../../shared/pools'

import { useReactToPrint } from 'react-to-print'
import PrintPools from './PrintPools'
import { poolResultColumns } from '../../../../shared/tables'

const TournamentPoolScoring = (props) => {
  const { height } = useWindowDimensions()
  const [pools, setPools] = useState([])
  const [scoredPools, setScoredPools] = useState([])
  const [config, setConfig] = useState({})
  const [selectedPool, setSelectedPool] = useState(0)
  const [isResultsVisible, setIsResultsVisible] = useState(false)
  const [isPrintVisible, setIsPrintVisible] = useState(false)
  const [resultList, setResultList] = useState([])
  const [printValue, setPrintValue] = useState('a')
  const printRef = useRef()
  const [tournament, setTournament] = useState({})
  const [isLoaded, setIsLoaded] = useState(false)
  const [advancedStats, setAdvancedStats] = useState(false)
  const [resultColumns, setResultColumns] = useState(poolResultColumns)
  const [isResultColumnSelectorVisible, setIsResultColumnSelectorVisible] = useState(false)

  useEffect(() => {
    const newTournament = props.tournament
    // clear next step
    const nextStep = props.step + 1
    let stepName = props.tournament.schedule[nextStep]
    if (stepName === 'bestof') {
      stepName = 'table'
    }
    delete newTournament[`${stepName}_${nextStep}`]
    setTournament(newTournament)
    let complete = true
    const previouslyScoredPools = []
    newTournament[`pools_${props.step}`].forEach(p => {
      if (p.complete) {
        previouslyScoredPools.push(p)
      }
      complete = complete && p.complete
    })

    setScoredPools(previouslyScoredPools)
    setPools(newTournament[`pools_${props.step}`])
    setConfig({
      duration: newTournament[`pool_duration_${props.step}`],
      name: newTournament[`pool_name_${props.step}`],
      promotion: newTournament[`pool_promotion_${props.step}`],
      promotion_exact: newTournament[`pool_promotion_exact_${props.step}`],
      score: newTournament[`pool_score_${props.step}`]
    })

    if (!complete) {
      props.onStart()
    }

    setAdvancedStats(newTournament[`pools_${props.step}`].some(p => p.isAdvancedEnabled))

    setIsLoaded(true)
    // eslint-disable-next-line
  }, [props.tournament])

  const saveTournamentData = (previouslyScoredPools, promoted, isLiveUpdate) => {
    const tournamentData = Object.assign({}, tournament)
    tournamentData[`pools_${props.step}`] = previouslyScoredPools
    if (promoted) {
      tournamentData.participants = promoted
    }
    props.onTournamentUpdate(tournamentData)
  }

  const handlePrintPools = useReactToPrint({
    content: () => printRef.current
  })

  const dynamicSizing = {
    y: height - 450
  }

  const handleSelectPool = (value) => {
    setSelectedPool(value)
  }

  const toParticipantList = (passedPools) => {
    const list = []

    // eslint-disable-next-line array-callback-return
    passedPools.map((pool, index) => {
      // eslint-disable-next-line array-callback-return
      pool.participants.map(participant => {
        list.push(Object.assign({}, participant, { pool: index + 1 }, { ...participant.stats }))
      })
    })

    list.sort((a, b) => {
      if (a?.stats?.status === b?.stats?.status) {
        if (a?.stats?.victories === b?.stats?.victories) {
          if (a?.stats?.victoryPercent === b?.stats?.victoryPercent) {
            if (a?.stats?.index === b?.stats?.index) {
              return a?.stats?.hitsScored > b?.stats?.hitsScored ? -1 : 1
            } else {
              return a?.stats?.index > b?.stats?.index ? -1 : 1
            }
          } else {
            return a?.stats?.victoryPercent > b?.stats?.victoryPercent ? -1 : 1
          }
        } else {
          return a?.stats?.victories > b?.stats?.victories ? -1 : 1
        }
      } else {
        return _.get(a, 'stats.status', 0) < b?.stats.status ? -1 : 1
      }
    })

    list.forEach((seed, index) => {
      if (index === 0) {
        seed.place = 1
      } else {
        if (seed.stats.victories === list[index - 1].stats.victories &&
          seed.stats.index === list[index - 1].stats.index &&
          seed.stats.hitsScored === list[index - 1].stats.hitsScored &&
          !seed.status) {
          seed.place = list[index - 1].place
        } else {
          seed.place = list[index - 1].place + 1
        }
      }
    })

    return list
  }

  const handlePoolScore = (pool) => {
    const copyPools = JSON.parse(JSON.stringify(pools))
    const newPools = copyPools.map((p, index) => {
      if (scoredPools[index]) {
        return scoredPools[index]
      }
      return p
    })
    newPools[selectedPool] = pool
    setScoredPools(newPools)
    let complete = true
    newPools.forEach(p => {
      complete = complete && p.complete
    })

    if (complete) {
      // get the final seeding list
      const list = toParticipantList(newPools)
      // determine how many advance
      let numberPromoted = list.length - 1
      if (config?.promotion !== 'all') {
        numberPromoted = _.parseInt(config?.promotion_exact) - 1
      }

      const promoted = list.map((p, index) => {
        if (index <= numberPromoted) {
          return Object.assign({}, p, { promoted: true, placeIndex: index })
        }
        return p
      })
      saveTournamentData(newPools, promoted)
      props.onFinish()
    } else {
      saveTournamentData(newPools)
    }
  }

  const handleResults = () => {
    const list = toParticipantList(scoredPools)
    setResultList(list)

    setIsResultsVisible(true)
  }

  const handleCloseModal = () => {
    setIsResultsVisible(false)
    setIsPrintVisible(false)
  }

  const handlePrintDialog = () => {
    setPrintValue('a')
    setIsPrintVisible(true)
  }

  const handlePrint = () => {
    handlePrintPools()
    setIsPrintVisible(false)
  }

  const handlePrintConfig = (e) => {
    setPrintValue(e.target.value)
  }

  const handleAdvancedEnabled = () => {
    const newPools = pools.map((p) => {
      return Object.assign({}, p, { isAdvancedEnabled: !advancedStats })
    })

    const helpKey = 'advancedStatsHelp'
    if (!advancedStats) {
      notification.open({
        message: 'Advance Statistics Entry',
        description: (
          <table>
            <tr>
              <th>Key</th>
              <th>Action</th>
            </tr>
            <tr>
              <td>a</td>
              <td>Assault</td>
            </tr>
            <tr>
              <td>c</td>
              <td>Clean Contact</td>
            </tr>
            <tr>
              <td>v</td>
              <td>Delayed Assault</td>
            </tr>
            <tr>
              <td>d</td>
              <td>Disarm</td>
            </tr>
            <tr>
              <td>f</td>
              <td>First Contact</td>
            </tr>
            <tr>
              <td>h</td>
              <td>Honor</td>
            </tr>
            <tr>
              <td>i</td>
              <td>Indirect</td>
            </tr>
            <tr>
              <td>n</td>
              <td>Intercept</td>
            </tr>
            <tr>
              <td>o</td>
              <td>Out of Bounds</td>
            </tr>
            <tr>
              <td>p</td>
              <td>Priority</td>
            </tr>
            <tr>
              <td>r</td>
              <td>Return</td>
            </tr>
            <tr>
              <td>s</td>
              <td>Second Violation</td>
            </tr>
            <tr>
              <td>t</td>
              <td>Third Violation</td>
            </tr>
          </table>
        ),
        duration: 0,
        key: helpKey
      })
    } else {
      notification.close(helpKey)
    }

    setAdvancedStats(!advancedStats)
    saveTournamentData(newPools)
  }

  const filterColumnsList = (value) => {
    setIsResultColumnSelectorVisible(!isResultColumnSelectorVisible)
  }

  const selectResultField = (e, value) => {
    const newFilters = JSON.parse(JSON.stringify(resultColumns))

    setResultColumns(newFilters.map(f => {
      if (value === f.dataIndex) {
        f.display = e.target.checked
      }

      return f
    }))
  }

  const handleLiveUpdate = (pool) => {
    const copyPools = JSON.parse(JSON.stringify(pools))
    const newPools = copyPools.map((p, index) => {
      if (scoredPools[index]) {
        return scoredPools[index]
      }
      return p
    })
    newPools[selectedPool] = pool
  }

  return (
    <div className={props.hidden ? 'hidden' : ''}>
      <div className='tournament-menu tournament-pools-scoring'>
        <Row gutter={[8]}>
          <Col flex='none'>
            <Select value={selectedPool} onChange={handleSelectPool}>
              {pools.map(p =>
                <Select.Option key={`pool_${p.index}`} value={p.index}>{squadronHelper.getColor(p.index)} Squadron</Select.Option>
              )}
            </Select>
          </Col>
          <Col flex='none'>
            {advancedStats
              ? (
                <Button block shape='round' onClick={handleAdvancedEnabled} type='warning'>
                  <FontAwesomeIcon icon={faAsterisk} style={{ paddingRight: '.5em' }} />
                  Disable Advanced Stats
                </Button>
                )
              : (
                <Button block shape='round' onClick={handleAdvancedEnabled} type='success'>
                  <FontAwesomeIcon icon={faAsterisk} style={{ paddingRight: '.5em' }} />
                  Enable Advanced Stats
                </Button>
                )}

          </Col>
          <Col flex='none'>
            <Button block shape='round' onClick={handlePrintDialog} type='info'>
              <PrinterOutlined />
              Print
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' onClick={handleResults} type='info'>
              <FontAwesomeIcon icon={faRankingStar} style={{ paddingRight: '.5em' }} />
              Results
            </Button>
          </Col>
        </Row>
      </div>
      <div>
        <PoolScoreMatrix uuid={props.tournament.uuid} round={`pools_${props.step}`} config={config} pool={pools?.[selectedPool]} scores={scoredPools?.[selectedPool]} onPoolScore={handlePoolScore} advancedStats={advancedStats} onLiveUpdate={handleLiveUpdate} />
      </div>
      <Modal
        title={(
          <div>
            <span>Pool Results</span>
            <div style={{ position: 'absolute', top: '1em', right: '5em' }}>
              <Dropdown
                overlay={<Menu
                  items={resultColumns.map(c => Object.assign({ label: <Checkbox checked={c.display} onChange={(e) => selectResultField(e, c.dataIndex)}>{c.title}</Checkbox>, key: c.dataIndex }))}
                         />}
                onVisibleChange={filterColumnsList}
                visible={isResultColumnSelectorVisible}
                trigger={['click']}
              >
                <a href onClick={(e) => e.preventDefault()}>
                  <Space>
                    Show/Hide columns
                    <DownOutlined />
                  </Space>
                </a>
              </Dropdown>
            </div>
          </div>
        )} visible={isResultsVisible} onCancel={handleCloseModal} maskClosable={false} destroyOnClose width={1024} footer={[
          <Button key='done' onClick={handleCloseModal}>
            Done
          </Button>
        ]}
      >
        <Table pagination={false} scroll={dynamicSizing} size='small' dataSource={resultList} columns={resultColumns.filter(c => c.display)} className='checkin-participants' />
      </Modal>
      <Modal
        title='Print Which Sheets?' visible={isPrintVisible} onCancel={handleCloseModal} onOk={handlePrint} maskClosable={false} destroyOnClose width={800}
      >
        <Radio.Group value={printValue} size='large' onChange={handlePrintConfig}>
          <Space direction='vertical'>
            <Radio value='a'>Current Squadron (All Sheets)</Radio>
            <Radio value='b'>Current Squadron (Just Grid)</Radio>
            <Radio value='c'>All Squadons (All Sheets)</Radio>
            <Radio value='d'>All Squadons (Just Grids)</Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <div style={{ display: 'none' }}>
        {isLoaded
          ? (
            <PrintPools config={config} pools={pools} pool={pools[selectedPool]} ref={printRef} sheetLayout={printValue} />
            )
          : ''}
      </div>
    </div>
  )
}

export default TournamentPoolScoring
